import React from 'react'
import { useQuery } from '@tanstack/react-query'
import ReactHTMLParser from 'react-html-parser'
import { Spin } from 'antd'

import { fetchFile } from '@pepper/utils/src/api/files/files'

import { useLoomEmbedHtml } from '../../hooks'
import { RenderFileProps } from './types'

import Doc from '../../assets/thumbnails/doc.png'
import File from '../../assets/thumbnails/file.png'
import PPT from '../../assets/thumbnails/ppt.png'
import PSD from '../../assets/thumbnails/psd.png'
import SVG from '../../assets/thumbnails/svg.png'
import XLSX from '../../assets/thumbnails/xlsx.png'
import Zip from '../../assets/thumbnails/zip.png'
import AI from '../../assets/thumbnails/ai.png'
import PDF from '../../assets/thumbnails/pdf.png'

import styles from './_.module.css'

const docMimeTypes = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]
const pptMimeTypes = [
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation'
]
const compressedMimeTypes = [
  'application/x-rar-compressed',
  'application/zip',
  'application/x-zip-compressed',
  'multipart/x-zip',
  'application/octet-stream'
]
const xlsxMimeTypes = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]
const PSDMime = ['image/vnd.adobe.photoshop', 'image/x-photoshop']
const svgMime = [
  'image/svg+xml',
  'image/x-coreldraw',
  'application/cdr',
  'application/coreldraw'
]
const aiMime = ['application/illustrator', 'application/postscript']
const pdfMime = ['application/pdf']

const RenderLoomLinks = ({ value }: { value: string }) => {
  const valueWithLoomEmbed = useLoomEmbedHtml(value)
  return (
    <div className={styles.loom}>{ReactHTMLParser(valueWithLoomEmbed)}</div>
  )
}

function RenderFile({ id, name, mimeType, url }: RenderFileProps): JSX.Element {
  const { data: thumbnail, isLoading } = useQuery({
    queryKey: [`/files/${id}`],
    queryFn: async () => {
      if (!id) return
      const response = await fetchFile({ id, showThumbnail: false })
      return URL.createObjectURL(response.data)
    },
    cacheTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: id !== null
  })

  // useEffect(() => {
  //   return () => {
  //     if (!thumbnail) return
  //     URL.revokeObjectURL(thumbnail)
  //   }
  // }, [])

  if (!mimeType && url) {
    return <RenderLoomLinks value={url} />
  }
  if (typeof mimeType !== 'string') return <img src={File} alt={name} />

  if (isLoading) return <Spin className={styles.spin} />

  if (mimeType.startsWith('video/')) {
    return (
      <video
        width="180px"
        height="180px"
        src={thumbnail}
        controls={true}
        autoPlay={false}
        muted={true}
      ></video>
    )
  }

  const onClick = () => {
    if (!window) return
    window.open(thumbnail || '', '_blank')
  }

  switch (true) {
    case docMimeTypes.includes(mimeType): {
      return (
        <img
          aria-hidden="true"
          loading="lazy"
          onClick={onClick}
          src={Doc}
          alt={name}
        />
      )
    }

    case pdfMime.includes(mimeType): {
      return (
        <img
          onClick={onClick}
          loading="lazy"
          aria-hidden="true"
          src={PDF}
          alt={name}
        />
      )
    }

    case xlsxMimeTypes.includes(mimeType): {
      return (
        <img
          onClick={onClick}
          loading="lazy"
          aria-hidden="true"
          src={XLSX}
          alt={name}
        />
      )
    }

    case PSDMime.includes(mimeType): {
      return (
        <img
          onClick={onClick}
          loading="lazy"
          aria-hidden="true"
          src={PSD}
          alt={name}
        />
      )
    }

    case pptMimeTypes.includes(mimeType): {
      return (
        <img
          onClick={onClick}
          loading="lazy"
          aria-hidden="true"
          src={PPT}
          alt={name}
        />
      )
    }

    case compressedMimeTypes.includes(mimeType): {
      return (
        <img
          onClick={onClick}
          loading="lazy"
          aria-hidden="true"
          src={Zip}
          alt={name}
        />
      )
    }

    case aiMime.includes(mimeType): {
      return (
        <img
          onClick={onClick}
          loading="lazy"
          aria-hidden="true"
          src={AI}
          alt={name}
        />
      )
    }

    case svgMime.includes(mimeType): {
      return (
        <img
          onClick={onClick}
          loading="lazy"
          aria-hidden="true"
          src={SVG}
          alt={name}
        />
      )
    }

    default:
      break
  }

  return (
    <img
      src={thumbnail}
      aria-hidden="true"
      onClick={onClick}
      loading="lazy"
      alt={name}
    />
  )
}

export default RenderFile
